import {Avatar, Button, Checkbox, DatePicker, Input, TimePicker} from 'antd'
import {getId, getType} from 'apis/model/base'
import classNames from 'classnames'
import {FormActionGroups} from 'components/form/FormActionBar'
import {Field, Section} from 'components/form/ModernForm'
import {WeekdaysField} from 'components/form/fields/WeekdaysField'
import {createSelectValue, createValue} from 'components/form/utils'
import {Formik, useFormikContext} from 'formik'
import {formatDate, getDateString, parseDayjs} from 'helpers/datetimeHelpers'
import {timezoneArray, timezoneAsDefault} from 'helpers/dateUtils'
import _ from 'lodash'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useMemo, useState} from 'react'
import {mapProps} from 'recompose'
import {SelectWidget} from 'views/AppLocals/custom/ReactSelectComponents'
import {getResponseItems, NullAsDefault, renderIf, shallowDiff} from 'views/Shared'
import LookupField from 'components/LookupField'
import {createControlledFormField} from 'views/AppLocals/factory/createFormField'
import {SubjectSelector} from 'views/AppLocals/Course/components/SubjectSelector'
import {TeacherSelector} from 'views/AppLocals/Course/components/TeacherSelector'
import {IoCalendarOutline} from 'react-icons/io5'
import Pure from 'components/Pure'
import dayjs from 'dayjs'
import {organization_getGroups_Api} from 'apis'
import useAsyncWithCache from 'modules/asyncCache/useAsyncWithCache'
import {IoIosTimer} from 'react-icons/io'
import {BsChevronRight, BsGeoAlt} from 'react-icons/bs'
import {
    PiArrowFatLineRightFill,
    PiListNumbersBold,
    PiMapPinLineFill,
    PiNotePencilFill,
    PiUsersThreeFill,
    PiWarningCircleFill
} from 'react-icons/pi'
import {BiSolidEdit, BiTrash} from 'react-icons/bi'
import {createInputModal, showDeleteConfirmDialog} from 'views/AppLocals/factory/createConfirmDialog'
import {ItemInformation} from 'components/common/ItemInformation'
import {dateAfter} from 'components/form/ValidateForm'
import {FaUserTie} from 'react-icons/fa6'
import {DoubleValueItem} from 'components/common/DoubleValueItem'
import {ActionModalWrapper} from 'components/form/ActionModalWrapper'
import {ErrorSummaries} from 'views/AppLocals/factory/createEditableForm'
import {GoDotFill} from 'react-icons/go'
import {RiMapPinTimeFill, RiStopCircleFill} from 'react-icons/ri'
import getTitle from 'helpers/getTitle'

const TimetableDetails = ({name, value, defaultValue = [], handleChange = NullAsDefault}) => {
    const t = useTranslate()
    const [errors, setErrors] = useState({})
    const [selectedIds, setSelectedIds] = useState(() => new Set())
    const [showPast, setShowPast] = useState(false)

    const timetable_details = value || defaultValue;
    const [newValue, setNewValue] = useState({
        start_time: dayjs().set('hour', 8).set('minute', 0),
        end_time: dayjs().set('hour', 8).set('minute', 0),
        location_address: null,
        id: Date.now()
    })

    const validateNewSession = () => {
        const errors = {}

        if (!newValue.start_time) {
            errors.start_time = t('required field')
        }
        if (!newValue.end_time) {
            errors.end_time = t('required field')
        }

        if (
            newValue.start_time &&
            newValue.end_time &&
            dayjs(newValue.end_time).isBefore(dayjs(newValue.start_time))
        ) {
            errors.end_time = t('end time must be after start time')
        }

        setErrors(errors)

        return _.isEmpty(errors)
    }

    // Sắp theo start_time (mảng gốc không đảm bảo thứ tự thời gian - phụ thuộc thứ tự bấm "thêm
    // buổi học") để đánh số thứ tự 1..N nhất quán bất kể buổi đó rơi vào nhóm sắp tới hay đã qua.
    const now = dayjs()
    const sortedDetails = useMemo(
        () => _.orderBy(timetable_details, [(d) => dayjs(d?.start_time).valueOf()], ['asc'])
            .map((d, i) => ({...d, __order: i + 1})),
        [timetable_details]
    )
    const upcomingDetails = useMemo(
        () => sortedDetails.filter((d) => !dayjs(d.start_time).isBefore(now)),
        [sortedDetails]
    )
    const pastDetails = useMemo(
        () => sortedDetails.filter((d) => dayjs(d.start_time).isBefore(now)),
        [sortedDetails]
    )
    const visibleDetails = showPast ? sortedDetails : upcomingDetails

    // selectedIds có thể còn giữ id của buổi học đã bị xóa qua icon xóa lẻ - lọc lại theo id đang
    // thật sự tồn tại để đếm/đối chiếu cho đúng, không cần dọn selectedIds ngay lúc xóa.
    const allIds = useMemo(() => new Set(timetable_details.map((d) => d?.id)), [timetable_details])
    const selectedCount = useMemo(
        () => Array.from(selectedIds).filter((id) => allIds.has(id)).length,
        [selectedIds, allIds]
    )

    const visibleIds = visibleDetails.map((d) => d.id)
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id))
    const someVisibleSelected = visibleIds.some((id) => selectedIds.has(id))

    const toggleSelect = (id) => setSelectedIds((prev) => {
        const next = new Set(prev)
        next.has(id) ? next.delete(id) : next.add(id)
        return next
    })

    // Select-all chỉ tác động các buổi ĐANG HIỂN THỊ (sắp tới, hoặc cả đã qua nếu đang mở rộng) -
    // không âm thầm chọn luôn các buổi đã qua đang bị ẩn.
    const toggleSelectAllVisible = () => setSelectedIds((prev) => {
        const next = new Set(prev)
        if (allVisibleSelected) {
            visibleIds.forEach((id) => next.delete(id))
        } else {
            visibleIds.forEach((id) => next.add(id))
        }
        return next
    })

    const handleBulkDelete = () => {
        showDeleteConfirmDialog({
            translate: t,
            title: 'delete selected sessions',
            // `showDeleteConfirmDialog` tự gọi translate(message) không kèm params (khác với
            // createConfirmDialog gốc có check typeof) - PHẢI truyền chuỗi đã interpolate {n} sẵn,
            // không truyền JSX. redux-i18n's t() coi mọi arg typeof 'object' (kể cả React element)
            // là plural-rule object rồi .split() lên undefined -> crash trắng màn hình khi bấm xoá.
            message: t('are you sure you want to delete {n} selected sessions', {n: selectedCount}),
            onOk: () => {
                const _timetable_details = _.cloneDeep(timetable_details)
                _.remove(_timetable_details, (item) => selectedIds.has(item?.id))
                handleChange(_timetable_details)
                setSelectedIds(new Set())
            },
        })
    }

    const renderDetailCard = (timetable_detail) => {
        const {id, __order, start_time, end_time, location_address, notes, is_holiday} = timetable_detail ?? {}
        const isHappened = dayjs(start_time).isBefore(now)
        const isSelected = selectedIds.has(id)

        return (
            <div key={id ?? __order} style={{padding: '6px 4px'}}
                 className={classNames(
                     "flex items-start gap-2 custom-item-render custom-render-border relative rounded-lg transition-colors duration-300",
                     isHappened && 'opacity-60',
                     isSelected && 'bg-blue-50'
                 )}>
                <Checkbox
                    className="mt-0.5 flex-shrink-0"
                    checked={isSelected}
                    onChange={() => toggleSelect(id)}
                />
                <div className="flex flex-col flex-1 min-w-0">
                    <div style={{fontSize: 12}}
                         className="font-bold text-color-000 capitalize tracking-wide leading-snug mr-6">
                        <span className="font-medium text-color-100 italic">
                            {`${__order}. ${parseDayjs(start_time)?.format("dddd")}, `}
                        </span>
                        {(start_time && end_time) ? (
                            dayjs(start_time).format('HH:mm') !== dayjs(end_time).format('HH:mm') ?
                            `${dayjs(start_time).format('HH:mm')} - ${dayjs(end_time).format('HH:mm')} - ` :
                                `${dayjs(start_time).format('HH:mm')} - `
                        ) : (
                            `${dayjs(start_time).format('HH:mm')} - `
                        )}
                        {formatDate(start_time, "DD/MM/YYYY")}
                    </div>
                    {renderIf(
                        is_holiday === 0,
                        <DoubleValueItem
                            showIcon={true}
                            labelFirst="time"
                            valueFirst={`${formatDate(start_time, 'HH:mm')} -> ${formatDate(end_time, 'HH:mm')}`}
                            labelSecond={location_address ? 'in' : ''}
                            valueSecond={
                                <span className="font-semibold text-blue-500">
                                {location_address}
                            </span>
                            }
                            style={{fontSize: 11}}
                            className="italic"
                        />
                    )}
                    {renderIf(
                        notes,
                        <span style={{fontSize: 11}} className="space-x-1 text-color-400 italic">
                            <GoDotFill size={8} className="mr-1"/>
                            {`${t('notes')}: `}
                            <span className="font-semibold text-color-100 whitespace-pre-line">
                                {notes}
                            </span>
                        </span>
                    )}
                </div>
                <div className="absolute top-2 right-2 flex gap-2">
                    <BiSolidEdit
                        size={13}
                        className="text-blue-500 hover:text-blue-700 cursor-pointer contextIcon"
                        onClick={() => {
                            let _timetable_details = _.cloneDeep(timetable_details);
                            const itemIndex = _timetable_details.findIndex(item => item.id === id);
                            if (itemIndex !== -1) {
                                createInputModal({
                                    title: 'add note',
                                    message: 'notes',
                                    placeholder: 'input your note',
                                    translate: t,
                                    value: _timetable_details[itemIndex]["notes"],
                                    onOk: (inputValue) => {
                                        _timetable_details[itemIndex]["notes"] = inputValue;
                                        handleChange(_timetable_details)
                                    },
                                });
                            }
                        }}
                    />
                    <BiTrash
                        size={13}
                        className="text-primary-200 hover:text-primary-700 cursor-pointer contextIcon"
                        onClick={() => {
                            let _timetable_details = _.cloneDeep(timetable_details);
                            showDeleteConfirmDialog({
                                translate: t,
                                title: 'delete lesson',
                                message: 'are you sure you want to delete lesson',
                                onOk: () => {
                                    _.remove(_timetable_details, {id});
                                    handleChange(_timetable_details)
                                    setSelectedIds((prev) => {
                                        const next = new Set(prev)
                                        next.delete(id)
                                        return next
                                    })
                                },
                            });
                        }}
                    />
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-3">
            <Section icon={IoCalendarOutline} title={t('add another session')}>
                <div className="flex flex-col flex-1 gap-2">
                    <div className="flex gap-3">
                        <div className="flex flex-col flex-1 gap-0.5">
                            <DatePicker
                                allowClear={true}
                                showTime={true}
                                showSecond={false}
                                placeholder={t('Select date')}
                                format="DD/MM/YYYY - HH:mm"
                                prefix={t('from')}
                                suffixIcon={<IoCalendarOutline className="text-color-500"/>}
                                value={newValue.start_time ? dayjs(newValue.start_time) : null}
                                onChange={(dateObj) => {
                                    if (dateObj) {
                                        setNewValue({...newValue, start_time: dateObj.local().format('YYYY-MM-DDTHH:mm:ssZ')})
                                    } else {
                                        setNewValue({...newValue, start_time: null})
                                    }
                                }}
                                style={{fontSize: 12, height: 32, maxHeight: 32}}
                                rootClassName="flex-1"
                            />
                            <div
                                className={classNames(
                                    "text-2xs text-red-500 italic transition-all duration-300",
                                    errors.start_time ? "opacity-100" : "opacity-0"
                                )}>
                                {errors.start_time}
                            </div>
                        </div>
                        <div className="flex flex-col flex-1 gap-0.5">
                            <DatePicker
                                allowClear={true}
                                showTime={true}
                                showSecond={false}
                                placeholder={t('Select date')}
                                format="DD/MM/YYYY - HH:mm"
                                prefix={t('to')}
                                suffixIcon={<IoCalendarOutline className="text-color-500"/>}
                                value={newValue.end_time ? dayjs(newValue.end_time) : null}
                                onChange={(dateObj) => {
                                    if (dateObj) {
                                        setNewValue({...newValue, end_time: dateObj.local().format('YYYY-MM-DDTHH:mm:ssZ')})
                                    } else {
                                        setNewValue({...newValue, end_time: null})
                                    }
                                }}
                                style={{fontSize: 12, height: 32, maxHeight: 32}}
                                rootClassName="flex-1"
                            />
                            <div
                                className={classNames(
                                    "text-2xs text-red-500 italic transition-all duration-300",
                                    errors.end_time ? "opacity-100" : "opacity-0"
                                )}>
                                {errors.end_time}
                            </div>
                        </div>
                        <Button
                            color="primary" variant="filled"
                            className="flex flex-center font-medium text-xs rounded-lg"
                            onClick={() => {
                                if (!validateNewSession()) {
                                    return
                                }
                                const _timetable_details = _.cloneDeep(timetable_details)
                                _timetable_details.push(newValue)
                                handleChange(_timetable_details)
                                setErrors({})
                                setNewValue({
                                    id: Date.now(),
                                    start_time: dayjs().set('hour', 8).set('minute', 0),
                                    end_time: dayjs().set('hour', 8).set('minute', 0),
                                    location_address: null,
                                    notes: '',
                                })
                            }}
                        >
                            {t('add session')}
                        </Button>
                    </div>
                    <div className="flex gap-3">
                        <Input
                            className="flex-1"
                            placeholder={t('enter note')}
                            onChange={(e) => {
                                setNewValue({...newValue, notes: e.target.value})
                            }}
                        />
                    </div>
                </div>
                <div className="font-semibold text-xs text-blue-600 italic tracking-wide">
                    <GoDotFill size={10} className="mr-0.5"/>
                    {`${t('tutorials')}: `}
                    <span className="font-normal">
                        {t('add another session description')}
                    </span>
                </div>
            </Section>

            <div className="flex flex-col gap-3 p-3 rounded-xl bg-background-50">
                {timetable_details.length > 0 && (
                    <div className="flex items-center justify-between gap-2">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                            <Checkbox
                                checked={allVisibleSelected}
                                indeterminate={!allVisibleSelected && someVisibleSelected}
                                onChange={toggleSelectAllVisible}
                            />
                            <span className="font-semibold text-2xs text-color-300 uppercase tracking-wider">
                                {selectedCount > 0 ? t('{n} selected', {n: selectedCount}) : t('select all')}
                            </span>
                        </label>
                        {selectedCount > 0 && (
                            <button
                                type="button"
                                onClick={handleBulkDelete}
                                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-2xs font-bold text-white bg-red-500 hover:bg-red-600 transition-colors duration-300">
                                <BiTrash size={12}/>
                                {t('delete selected')}
                            </button>
                        )}
                    </div>
                )}

                {upcomingDetails.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {upcomingDetails.map(renderDetailCard)}
                    </div>
                ) : (
                    <div className="py-3 flex flex-col items-center gap-1 text-center">
                        <span className="text-xs text-color-300 italic">{t('no upcoming sessions')}</span>
                    </div>
                )}

                {pastDetails.length > 0 && (
                    <div className="flex flex-col gap-2 pt-1 border-t border-color-100">
                        <button
                            type="button"
                            onClick={() => setShowPast((s) => !s)}
                            className="self-start mt-2 flex items-center gap-1 text-2xs font-bold text-color-300 hover:text-blue-600 uppercase tracking-wider">
                            <BsChevronRight
                                size={10}
                                style={{transform: showPast ? 'rotate(90deg)' : undefined, transition: 'transform 0.2s'}}
                            />
                            {showPast ? t('hide past sessions') : `${t('view past sessions')} (${pastDetails.length})`}
                        </button>
                        {showPast && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {pastDetails.map(renderDetailCard)}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

// `organization` trong values có thể là bản OrganizationTransformerBasic (vd lấy qua Group->organization)
// nên KHÔNG có field `.groups` — luôn fetch riêng qua organization_getDepartments_Api theo id, không đọc
// values.organization.groups (mirror ParentDepartmentField trong OrganizationGroupForm.js).
const GroupInOrganizationField = ({name, value, onChange, form}) => {
    const t = useTranslate()
    const organization = _.get(form, 'values.organization')
    const org_id = getId(organization)

    const {response, isLoading} = useAsyncWithCache({
        cacheId: [organization_getGroups_Api.path, org_id].join('/'),
        apiInfo: organization_getGroups_Api,
        query: {id: org_id},
        once: true,
    })

    const options = useMemo(() => {
        const items = getResponseItems(response) || []
        return items.map((e) => ({
            value: e.id, label: `${e?.title} (${e?.member_count} ${t('members')})`
        }))
    }, [response, t])

    return (
        <SelectWidget
            name={name}
            allowClear={true}
            loading={isLoading}
            className="w-full"
            value={value || undefined}
            options={options}
            placeholder={t('---Choose One---')}
            onChange={(v) => onChange(createSelectValue('group_id', v))}
        />
    )
}

// `values.courseOwnerId`/`values.courseOwnerType` (course_class only) là owner CỦA COURSE_CLASS
// (vd organization sở hữu khóa học, dùng để filter Teacher/Subject) — cố tình đặt tên khác
// `owner_type`/`owner_id` (Timetable thuộc về organization hay course_class, do useTimetableOrganizationForm/
// useTimetableCourseClassForm tự thêm khi gọi API) để không bao giờ đụng field, tránh leak nhầm giá trị.
const formSchema = {
    title: {
        title: 'title',
        children: [
            {
                name: 'title',
                component: createControlledFormField({placeholder: 'title'}),
            },
        ],
    },
    phase: {
        title: 'phase title',
        description: 'phase title sort hint',
        children: [
            {
                name: 'phase_title',
                component: createControlledFormField({placeholder: 'phase title placeholder'}),
            },
        ],
    },
    from_to_date: {
        inline: true,
        description: 'timetable display period',
        className: "grid grid-cols-2 gap-3",
        children: [
            {
                label: 'from date',
                name: 'from_date',
                component: ({name, value, onChange}) => (
                    <Translate>
                        {(t) => (
                            <DatePicker
                                allowClear={true}
                                showTime={false}
                                className="w-full"
                                placeholder={t('Select date')}
                                format="DD/MM/YYYY"
                                suffixIcon={<IoCalendarOutline className="text-color-500"/>}
                                defaultValue={value ?? undefined}
                                onChange={(value) => onChange(createValue(name, value))}
                            />
                        )}
                    </Translate>
                ),
            },
            {
                label: 'to date',
                name: 'to_date',
                component: ({name, value, onChange}) => (
                    <Translate>
                        {(t) => (
                            <DatePicker
                                showTime={false}
                                showSecond={false}
                                className="w-full"
                                placeholder={t('Select date')}
                                allowClear={true}
                                format="DD/MM/YYYY"
                                suffixIcon={<IoCalendarOutline className="text-color-500"/>}
                                defaultValue={value ?? undefined}
                                onChange={(value) => onChange(createValue(name, value))}
                            />
                        )}
                    </Translate>
                ),
            },
        ],
    },
    timezone: {
        title: 'timezone',
        description: 'timezone applied to start time / end time below',
        children: [
            {
                name: 'timezone',
                component: ({name, value, onChange}) => (
                    <SelectWidget
                        name={name}
                        className="w-full"
                        allowClear={false}
                        value={value}
                        options={timezoneArray.map(({label, value}) => ({label: `${label} — ${value}`, value}))}
                        onChange={(v) => onChange(createValue(name, v ?? timezoneAsDefault))}
                    />
                ),
            },
        ],
    },
    days_of_week: {
        title: 'day of week',
        description: 'recurring days of the week',
        children: [
            {
                name: 'days_of_week',
                component: createControlledFormField({
                    className: 'w-full',
                    Component: WeekdaysField,
                    withProps: ({name, value, onChange}) => ({
                        defaultValue: value,
                        onChange: (_value) => onChange(createValue(name, _value)),
                    }),
                    withValues: ({resetKey}) => ({resetKey}),
                }),
            },
        ],
    },

    from_to_time: {
        inline: true,
        description: 'recurring session time',
        className: 'grid grid-cols-2 gap-3',
        children: [
            {
                label: 'start time',
                name: 'from_time',
                component: ({name, value, onChange}) => (
                    <TimePicker
                        className="w-full"
                        allowClear={true}
                        placeholder="07:00"
                        format="HH:mm"
                        suffixIcon={<IoIosTimer className="text-color-500"/>}
                        defaultValue={value ? dayjs(value, 'HH:mm') : undefined}
                        onChange={(value) => onChange(createValue(name, value))}
                    />
                ),
            },
            {
                label: 'end time',
                name: 'to_time',
                component: ({name, value, onChange}) => (
                    <Translate>
                        {() => (
                            <TimePicker
                                className="w-full"
                                placeholder="12:00"
                                allowClear={false}
                                format="HH:mm"
                                suffixIcon={<IoIosTimer className="text-color-500"/>}
                                defaultValue={value ? dayjs(value, 'HH:mm') : undefined}
                                onChange={(value) => onChange(createValue(name, value))}
                            />
                        )}
                    </Translate>
                ),
            },
        ],
    },
    location_address: {
        title: 'locations',
        children: [
            {
                name: 'location_address',
                component: ({name, value, onChange, form}) => {
                    const values = _.get(form, 'values', {})
                    // organization: owner lấy từ values.organization. group: owner lấy từ values.group
                    // (standalone group, không thuộc organization). course_class: cả 2 field trên không
                    // tồn tại nên fallback sang values.courseOwnerType/courseOwnerId (owner của
                    // course_class, do Creation/Editing/Copy modal set sẵn — cùng field TeacherSelector đọc).
                    const organizationOwner = _.get(values, 'organization', {})
                    const groupOwner = _.get(values, 'group', {})
                    const owner_type = getType(organizationOwner) || getType(groupOwner) || values?.courseOwnerType;
                    const owner_id = organizationOwner?.id || groupOwner?.id || values?.courseOwnerId;
                    return (
                        <Translate>
                            {(t) => (
                                <LookupField
                                    createIfNotExists={true}
                                    defaultValue={values?.location_address}
                                    apiPath={`/lookup/${owner_type}/${owner_id}/field/location_address/gets`}
                                    name={name}
                                    placeholder={t('choose or create address')}
                                    handleChange={(e) => onChange(createValue('location_address', e.target.value))}
                                    OptionWrapper={({children, ...props}) =>
                                        <div className="lookup-item"
                                            {...props}>
                                            {children}
                                        </div>
                                    }
                                    renderOption={(o, i) => (
                                        <div key={i} className="flex items-center flex-1 gap-2 md:gap-3">
                                            <Avatar
                                                size={50}
                                                className="flex flex-center bg-secondary-100 rounded-xl"
                                                icon={<BsGeoAlt size={20} className="text-secondary"/>}
                                            />
                                            <div className="flex flex-col gap-1 flex-1">
                                                <span className="font-bold text-sm text-color-000 leading-snug">
                                                    {o.label}
                                                </span>
                                            </div>
                                            <BsChevronRight size={16} className="text-color-500"/>
                                        </div>
                                    )}
                                />
                            )}
                        </Translate>
                    )
                },
            },
        ],
    },
    teacher: {
        title: 'teacher',
        children: [
            {
                name: 'teacher',
                component: mapProps(({name, value, form, onChange}) => {
                    const {courseOwnerId, courseOwnerType} = form?.values ?? {}
                    return {name, value, onChange, owner_id: courseOwnerId, owner_type: courseOwnerType}
                })(TeacherSelector),
            },
        ],
    },
    subject: {
        title: 'subjects',
        children: [
            {
                name: 'subject',
                // Lấy môn học từ Khóa (course_getCourseSubjects_Api - mặc định của SubjectSelector,
                // theo course_id) thay vì chỉ những môn ĐÃ gán cho Lớp (course_getCourseClassSubjects_Api)
                // - chọn môn nào ở đây thì TimetableController@create/update backend tự động gán
                // (link) môn đó vào Lớp luôn, không cần bó hẹp lựa chọn theo những gì đã gán sẵn.
                component: mapProps(({name, value, form, onChange}) => ({
                    name,
                    value,
                    onChange,
                    course_id: form?.values?.course_id,
                }))(SubjectSelector),
            },
        ],
    },
    department_in_organization: {
        description: 'which group or ministry this timetable belongs to',
        children: [
            {
                name: 'group_id',
                component: GroupInOrganizationField,
            },
        ],
    },
    timetable_details: {
        children: [
            {
                name: 'timetable_details',
                component: createControlledFormField({
                    className: 'w-full',
                    Component: TimetableDetails,
                    withProps: ({name, value, onChange}) => ({
                        defaultValue: value,
                        handleChange: (_value) => onChange(createValue(name, _value)),
                    }),
                    withValues: ({resetKey}) => ({resetKey}),
                }),
            },
        ],
    },
    notes: {
        title: 'notes',
        children: [
            {
                name: 'notes',
                component: createControlledFormField({
                    rows: 3,
                    className: 'w-full',
                    placeholder: 'notes',
                    Component: Input.TextArea,
                }),
            },
        ],
    },
}

// isCourseClass: teacher/subject chỉ tồn tại trên form khi owner_type='course_class'
// (xem SubjectTeacherSection) — bắt buộc 2 field này với owner_type='group'/'organization'
// sẽ khiến isValid luôn false vì chúng luôn undefined, nút submit bị khoá vĩnh viễn.
const validate = (values, t, isCourseClass) => {
    let errors = {}
    const {phase_title, from_date, to_date, from_time, to_time, is_holiday, teacher, subject} = values ?? {}

    if (!/^[\p{L}\p{N}\s]+$/u.test(phase_title)) {
        errors.phase_title = t('no special chars')
    }
    if (!from_date) errors.from_date = t('required field')
    if (!to_date) errors.to_date = t('required field')
    // inclusive=true - 1 giai đoạn có thể chỉ diễn ra trong 1 ngày (from_date=to_date hợp lệ), khác
    // CourseSetForm.js (ngày kết thúc khóa học phải SAU ngày bắt đầu, không cho bằng).
    const toDateError = from_date && dateAfter(from_date, t, {inclusive: true})(to_date)
    if (toDateError) errors.to_date = toDateError
    if (is_holiday === 0 && !from_time) errors.from_time = t('required field')
    if (is_holiday === 0 && !to_time) errors.to_time = t('required field')

    if (isCourseClass) {
        if (!teacher) errors.teacher = t('required field')
        if (!subject) errors.subject = t('required field')
    }

    return errors
}

// ---------------------------------------------------------------------------
// Modern form layout (Section/Field, xem components/form/ModernForm.js) — TÁI DÙNG NGUYÊN VẸN các
// field component đã định nghĩa trong `formSchema` ở trên (đã có sẵn logic value/onChange/owner_type
// resolution phức tạp: GroupInOrganizationField, location_address, TeacherSelector/SubjectSelector,
// WeekdaysField...). Section/Field bên dưới CHỈ đổi layout hiển thị — gọi thẳng
// `formSchema.x.children[i].component` với props {name, value, onChange, form} lấy từ
// useFormikContext(), giống hệt cách FieldsFactory gọi field bên trong (xem FieldsFactory.js
// renderChildren) — không viết lại logic value/onChange, tránh phải test lại toàn bộ 3 owner_type
// (organization/group/course_class) x 3 modal (Creation/Editing/Copy). Xem docs/ui/form-layout.md.
// ---------------------------------------------------------------------------

const FieldHint = ({children}) => (
    <span className="-mt-2 text-2xs text-color-300 italic">{children}</span>
)

const BasicInfoSection = ({isCourseClass, collapsible = false}) => {
    const t = useTranslate()
    const formik = useFormikContext()
    const {values, errors} = formik
    const titleSchema = isCourseClass ? formSchema.phase : formSchema.title
    const TitleField = titleSchema.children[0].component
    const titleName = titleSchema.children[0].name
    const FromDateField = formSchema.from_to_date.children[0].component
    const ToDateField = formSchema.from_to_date.children[1].component

    return (
        <Section icon={PiWarningCircleFill} title={t('basic information')} collapsible={collapsible}>
            <Field label={t(titleSchema.title)} required error={errors[titleName]}>
                <TitleField name={titleName} value={values[titleName]} onChange={formik.handleChange} form={formik}/>
            </Field>
            {renderIf(isCourseClass, <FieldHint>{t(titleSchema.description)}</FieldHint>)}
            <div className="grid grid-cols-2 gap-3">
                <Field label={t('from date')} required error={errors.from_date}>
                    <FromDateField name="from_date" value={values.from_date} onChange={formik.handleChange}/>
                </Field>
                <Field label={t('to date')} required error={errors.to_date}>
                    <ToDateField name="to_date" value={values.to_date} onChange={formik.handleChange}/>
                </Field>
            </div>
            <FieldHint>{t(formSchema.from_to_date.description)}</FieldHint>
        </Section>
    )
}

const GroupPickerSection = () => {
    const t = useTranslate()
    const formik = useFormikContext()
    const GroupField = formSchema.department_in_organization.children[0].component
    return (
        <Section
            icon={PiUsersThreeFill}
            title={t('choose group / ministry')}
            subtitle={t(formSchema.department_in_organization.description)}>
            <GroupField name="group_id" value={formik.values.group_id} onChange={formik.handleChange} form={formik}/>
        </Section>
    )
}

// Highlight (nền nhấn) - đây là nội dung THỰC SỰ định nghĩa lịch lặp lại (thứ trong tuần/giờ/địa
// điểm), quan trọng hơn phần "thông tin cơ bản" (tựa đề/khoảng ngày áp dụng) chỉ mang tính mô tả.
const ScheduleSection = () => {
    const t = useTranslate()
    const formik = useFormikContext()
    const {values, errors} = formik
    const WeekdaysComp = formSchema.days_of_week.children[0].component
    const FromTimeField = formSchema.from_to_time.children[0].component
    const ToTimeField = formSchema.from_to_time.children[1].component
    const TimezoneField = formSchema.timezone.children[0].component
    const LocationField = formSchema.location_address.children[0].component

    return (
        <Section icon={PiMapPinLineFill} title={`${t('time')} / ${t('locations')}`} theme="highlight">
            <Field label={t(formSchema.days_of_week.title)}>
                <WeekdaysComp name="days_of_week" value={values.days_of_week} onChange={formik.handleChange} form={formik}/>
            </Field>
            <FieldHint>{t(formSchema.days_of_week.description)}</FieldHint>
            <div className="grid grid-cols-2 gap-3">
                <Field label={t('start time')} required error={errors.from_time}>
                    <FromTimeField name="from_time" value={values.from_time} onChange={formik.handleChange}/>
                </Field>
                <Field label={t('end time')} required error={errors.to_time}>
                    <ToTimeField name="to_time" value={values.to_time} onChange={formik.handleChange}/>
                </Field>
            </div>
            <FieldHint>{t(formSchema.from_to_time.description)}</FieldHint>
            <Field label={t(formSchema.timezone.title)}>
                <TimezoneField name="timezone" value={values.timezone} onChange={formik.handleChange}/>
            </Field>
            <FieldHint>{t(formSchema.timezone.description)}</FieldHint>
            <Field label={t(formSchema.location_address.title)}>
                <LocationField name="location_address" value={values.location_address} onChange={formik.handleChange} form={formik}/>
            </Field>
        </Section>
    )
}

const SubjectTeacherSection = () => {
    const t = useTranslate()
    const formik = useFormikContext()
    const {values, errors} = formik
    const SubjectField = formSchema.subject.children[0].component
    const TeacherField = formSchema.teacher.children[0].component
    const NotesField = formSchema.notes.children[0].component
    return (
        <Section icon={FaUserTie} title={`${t('subjects')} / ${t('teachers')}`}>
            <Field label={t(formSchema.subject.title)} required error={errors.subject}>
                <SubjectField name="subject" value={values.subject} onChange={formik.handleChange} form={formik}/>
            </Field>
            <Field label={t(formSchema.teacher.title)} required error={errors.teacher}>
                <TeacherField name="teacher" value={values.teacher} onChange={formik.handleChange} form={formik}/>
            </Field>
            <Field label={t(formSchema.notes.title)}>
                <NotesField name="notes" value={values.notes} onChange={formik.handleChange} form={formik}/>
            </Field>
        </Section>
    )
}

const NotesSection = () => {
    const t = useTranslate()
    const formik = useFormikContext()
    const NotesField = formSchema.notes.children[0].component
    return (
        <Section icon={PiNotePencilFill} title={t(formSchema.notes.title)}>
            <NotesField name="notes" value={formik.values.notes} onChange={formik.handleChange} form={formik}/>
        </Section>
    )
}

const TimetableDetailsSection = () => {
    const t = useTranslate()
    const formik = useFormikContext()
    const DetailsField = formSchema.timetable_details.children[0].component
    return (
        <div className="flex flex-col gap-2 pt-2">
            <div className="flex items-center gap-1.5">
                <PiListNumbersBold size={15} className="text-color-500"/>
                <span className="font-bold text-base text-color-300 uppercase tracking-wider">
                    {`${t('detail')} ${t('timetables')}`}
                </span>
            </div>
            <DetailsField
                name="timetable_details"
                value={formik.values.timetable_details}
                onChange={formik.handleChange}
                form={formik}
            />
        </div>
    )
}

export const TimetableCreationModal = ({
                                           owner_type,
                                           organization,
                                           group,
                                           defaultGroupId,
                                           course_id,
                                           course_class_id,
                                           courseOwnerType,
                                           courseOwnerId,
                                           onSubmit = NullAsDefault,
                                           submitProps = {}
                                       }) => {
    const t = useTranslate()
    const isCourseClass = owner_type === 'course_class'
    // owner_type='organization': timetable của 1 department trong org, cần picker chọn group/ministry con.
    // owner_type='group': timetable của 1 group độc lập (không thuộc org) - context group đã cố định,
    // không cần picker.
    const isDepartmentOfOrganization = owner_type === 'organization'
    const [resetKey, setResetKey] = useState();

    const initialValues = useMemo(() => {
        const base = {
            from_date: undefined, to_date: undefined,
            from_time: undefined, to_time: undefined,
            days_of_week: '0000000',
            timezone: timezoneAsDefault,
            is_holiday: 0,
        }
        switch (owner_type) {
            case 'course_class':
                return {
                    ...base,
                    course_id,
                    course_class_id,
                    courseOwnerId,
                    courseOwnerType,
                    teacher: undefined,
                    subject: undefined,
                }
            case 'group':
                return {
                    ...base,
                    group,
                }
            case 'organization':
            default:
                return {
                    ...base,
                    organization,
                    group_id: defaultGroupId,
                }
        }
    }, [owner_type, courseOwnerType, courseOwnerId, organization, group, defaultGroupId, course_id, course_class_id])

    return (
        <Formik
            onSubmit={(values) => {
                const {subject, teacher, courseOwnerId, courseOwnerType, organization, group, from_date, to_date, from_time, to_time, ...rest} = values ?? {}
                onSubmit(
                    _.omitBy(
                        {
                            ...rest,
                            from_date: getDateString(from_date),
                            to_date: getDateString(to_date),
                            from_time: from_time ? dayjs(from_time).format("HH:mm") : null,
                            to_time: to_time ? dayjs(to_time).format("HH:mm") : null,
                            ...(isCourseClass ? {
                                teacher_id: values?.teacher ? getId(teacher) : null,
                                subject_id: values?.subject ? getId(subject) : null,
                            } : {
                                organization_id: getId(organization),
                            }),
                        },
                        _.isUndefined
                    )
                )
            }}
            onReset={() => setResetKey(Date.now())}
            validate={(values) => validate(values, t, isCourseClass)}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={initialValues}>
            <React.Fragment>
                <ErrorSummaries/>
                <ActionModalWrapper action={<FormActionGroups submitProps={submitProps}/>}>
                    <Pure input={[resetKey]}>
                        <div className="flex flex-col gap-3">
                            <BasicInfoSection isCourseClass={isCourseClass}/>
                            {isDepartmentOfOrganization && <GroupPickerSection/>}
                            <ScheduleSection/>
                            {isCourseClass ? <SubjectTeacherSection/> : <NotesSection/>}
                        </div>
                    </Pure>
                </ActionModalWrapper>
            </React.Fragment>
        </Formik>
    )
}

export const TimetableEditingModal = ({
                                          owner_type,
                                          organization,
                                          group,
                                          course_id,
                                          course_class_id,
                                          courseOwnerType,
                                          courseOwnerId,
                                          item,
                                          onSubmit = NullAsDefault,
                                          submitProps = {}
                                      }) => {
    const t = useTranslate()
    const isCourseClass = owner_type === 'course_class'
    const isDepartmentOfOrganization = owner_type === 'organization'

    const initialValues = useMemo(() => {
        const {from_date, to_date, ...rest} = item ?? {}
        const base = {
            ...rest,
            from_date: parseDayjs(from_date),
            to_date: parseDayjs(to_date),
        }
        switch (owner_type) {
            case 'course_class':
                return {
                    ...base,
                    course_id,
                    course_class_id,
                    courseOwnerId,
                    courseOwnerType,
                }
            case 'group':
                return {
                    ...base,
                    group: rest?.group ?? group,
                }
            case 'organization':
            default:
                return {
                    ...base,
                    group_id: rest?.group?.id,
                    organization,
                }
        }
    }, [owner_type, courseOwnerType, courseOwnerId, organization, group, course_id, course_class_id, item])

    return (
        <Formik
            onSubmit={(values) => {
                const changes = shallowDiff(values, initialValues)
                const {subject, teacher, courseOwnerId, courseOwnerType, organization, group, from_date, to_date, from_time, to_time, timetable_details, ...rest} = changes ?? {}
                onSubmit(
                    _.omitBy(
                        {
                            ...rest,
                            from_date: getDateString(from_date),
                            to_date: getDateString(to_date),
                            from_time: !!from_time ? dayjs(from_time).format("HH:mm") : undefined,
                            to_time: !!to_time ? dayjs(to_time).format("HH:mm") : undefined,
                            timetable_details: JSON.stringify(timetable_details),
                            ...(isCourseClass ? {
                                teacher_id: values?.teacher ? getId(teacher) : null,
                                subject_id: values?.subject ? getId(subject) : null,
                            } : {}),
                        },
                        _.isUndefined
                    )
                )
            }}
            validate={(values) => validate(values, t, isCourseClass)}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={initialValues}>
            <React.Fragment>
                <ErrorSummaries/>
                <ActionModalWrapper action={<FormActionGroups submitProps={submitProps}/>}>
                    <div className="flex flex-col gap-3">
                        <BasicInfoSection isCourseClass={isCourseClass} collapsible={true}/>
                        {isDepartmentOfOrganization && <GroupPickerSection/>}
                        <ScheduleSection/>
                        {isCourseClass && <SubjectTeacherSection/>}
                        {!isCourseClass && <NotesSection/>}
                        <TimetableDetailsSection/>
                    </div>
                </ActionModalWrapper>
            </React.Fragment>
        </Formik>
    )
}

export const TimetableCopyModal = ({
                                       owner_type,
                                       organization,
                                       group,
                                       course_id,
                                       course_class_id,
                                       courseOwnerType,
                                       courseOwnerId,
                                       item,
                                       onSubmit = NullAsDefault,
                                       submitProps = {}
                                   }) => {
    const t = useTranslate()
    const isCourseClass = owner_type === 'course_class'
    const isDepartmentOfOrganization = owner_type === 'organization'

    const initialValues = useMemo(() => {
        const {id, from_date, to_date, ...rest} = item ?? {}
        const base = {
            ...rest,
            from_date: parseDayjs(from_date),
            to_date: parseDayjs(to_date),
        }
        switch (owner_type) {
            case 'course_class':
                return {
                    ...base,
                    subject_id: rest?.subject?.id,
                    course_id,
                    course_class_id,
                    courseOwnerId,
                    courseOwnerType,
                }
            case 'group':
                return {
                    ...base,
                    group: rest?.group ?? group,
                }
            case 'organization':
            default:
                return {
                    ...base,
                    group_id: rest?.group?.id,
                    organization,
                }
        }
    }, [owner_type, courseOwnerType, courseOwnerId, organization, group, course_id, course_class_id, item])

    return (
        <Formik
            onSubmit={(values) => {
                const {subject, teacher, courseOwnerId, courseOwnerType, organization, group, from_date, to_date, from_time, to_time, timetable_details, ...rest} = values ?? {}
                onSubmit(
                    _.omitBy(
                        {
                            ...rest,
                            from_date: getDateString(from_date),
                            to_date: getDateString(to_date),
                            from_time: !!from_time ? dayjs(from_time, "HH:mm").format("HH:mm") : undefined,
                            to_time: !!to_time ? dayjs(to_time, "HH:mm").format("HH:mm") : undefined,
                            timetable_details: JSON.stringify(timetable_details),
                            ...(isCourseClass ? {
                                teacher_id: values?.teacher ? getId(teacher) : null,
                                subject_id: values?.subject ? getId(subject) : null,
                            } : {
                                organization_id: getId(organization),
                            }),
                        },
                        _.isUndefined
                    )
                )
            }}
            validate={(values) => validate(values, t, isCourseClass)}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={initialValues}>
            <React.Fragment>
                <ErrorSummaries/>
                <ActionModalWrapper action={<FormActionGroups submitProps={submitProps}/>}>
                    <div className="flex flex-col gap-3">
                        <BasicInfoSection isCourseClass={isCourseClass} collapsible={true}/>
                        {isDepartmentOfOrganization && <GroupPickerSection/>}
                        <ScheduleSection/>
                        {isCourseClass ? <SubjectTeacherSection/> : <NotesSection/>}
                    </div>
                </ActionModalWrapper>
            </React.Fragment>
        </Formik>
    )
}

export const renderTimetableItem = ({renderContextMenu = NullAsDefault} = {}) => (item, index) => {
    const {group, from_date, to_date, title, timetable_details = [], location_address, notes, is_holiday, phase_title, subject, teacher} = item ?? {}

    const displayTitle = phase_title ?? title

    function getNextLesson(timetables) {
        const currentDate = dayjs();
        const futureDays = timetables.filter(day => dayjs(day.start_time).isAfter(currentDate));
        if (futureDays.length === 0) return false
        return futureDays.reduce((nearest, day) => {
            return dayjs(day.start_time).isBefore(dayjs(nearest.start_time)) ? day : nearest;
        }).start_time;
    }

    let one_day = formatDate(from_date, 'DD/MM/YYYY') === formatDate(to_date, 'DD/MM/YYYY')

    let is_finished = one_day
        ? dayjs(from_date).isBefore(dayjs())
        : getNextLesson(timetable_details) === false

    const TitleLesson = ({day}) => {
        const t = useTranslate()
        return (
            <span className="space-x-1 text-xs italic leading-snug">
                <span className="text-color-500 whitespace-no-wrap">{`${t('schedule')} : `}</span>
                {renderIf(
                    !one_day,
                    <span className="font-bold text-blue-600">
                        {`${_.capitalize(formatDate(day, 'dddd'))}, ${formatDate(day, "DD/MM/YYYY")} `}
                    </span>
                )}
                {renderIf(
                    location_address,
                    <span className="mx-1 text-color-500 whitespace-no-wrap">{t('in')}</span>
                )}
                {renderIf(
                    location_address,
                    <span className="font-semibold text-color-100">{location_address}</span>
                )}
            </span>
        )
    }

    return (
        <Translate key={index}>
            {(t) => (
                <div className={classNames(
                    "p-3 flex flex-col gap-1 custom-item-render relative list-item",
                    is_finished && 'opacity-60'
                )}>
                    <div className="flex flex-col gap-x-1">
                        <div className={classNames(
                            "mb-2 flex items-center gap-1 leading-tight mr-6",
                            is_finished
                                ? 'px-2 py-1.5 rounded-lg background-100'
                                : 'pb-2 border-b border-color-50'
                        )}>
                            {is_holiday === 1
                                ? <RiStopCircleFill size={15} className="text-primary-500"/>
                                : <RiMapPinTimeFill size={15} className={is_finished ? 'text-color-500' : 'text-green-500'}/>}
                            {one_day ? (
                                <ItemInformation
                                    primary={true}
                                    colon={false}
                                    label="date"
                                    value={formatDate(from_date, 'DD/MM/YYYY')}
                                    className="text-sm italic"
                                />
                            ) : (
                                <DoubleValueItem
                                    primary={true}
                                    colon={false}
                                    labelFirst="from date"
                                    valueFirst={formatDate(from_date, 'DD/MM/YYYY')}
                                    labelSecond={to_date ? 'to date' : ''}
                                    valueSecond={formatDate(to_date, 'DD/MM/YYYY')}
                                    className="text-sm italic"
                                />
                            )}
                        </div>

                        {displayTitle !== null && (
                            <span className="font-bold text-sm text-gray-700 tracking-wide">
                                {displayTitle}
                            </span>
                        )}
                        {is_holiday === 1 ? (
                            <div className="ml-3 mt-1 flex flex-col">
                                <div className="flex items-center gap-3">
                                    <PiArrowFatLineRightFill size={11} className="text-primary-300"/>
                                    <span className="font-medium text-xs text-primary-400 italic tracking-widest">
                                        {t('holiday')}
                                    </span>
                                </div>
                                {renderIf(
                                    notes,
                                    <div className="flex items-start gap-1 text-xs italic">
                                        <span className="flex items-center gap-1 text-color-500 whitespace-no-wrap">
                                            <GoDotFill size={10} className="text-color-500"/>
                                            {`${t('note')} : `}
                                        </span>
                                        <span className="font-semibold text-color-100 whitespace-pre-line">
                                            {notes}
                                        </span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div style={{marginLeft: 2, marginTop: 2}} className="flex flex-col gap-1">
                                <div className="space-x-1 leading-snug">
                                    {is_finished
                                        ? <PiArrowFatLineRightFill size={11} className="text-primary-600"/>
                                        : <GoDotFill size={10} className="text-color-400"/>}
                                    {is_finished
                                        ? <span className="font-bold text-xs text-primary-600 italic">{t('finished')}</span>
                                        : <TitleLesson day={getNextLesson(timetable_details)}/>}
                                </div>
                                {group && (
                                    <ItemInformation
                                        showIcon={true}
                                        label="group / ministry type"
                                        value={getTitle(group)}
                                        className="text-xs italic"
                                    />
                                )}
                                {teacher && (
                                    <ItemInformation
                                        showIcon={true}
                                        label="teacher"
                                        value={`${teacher?.prefix_title || ''} ${teacher?.fullname || getTitle(teacher)}`}
                                        className="text-xs italic"
                                    />
                                )}
                                {subject && (
                                    <ItemInformation
                                        showIcon={true}
                                        label="subjects"
                                        value={getTitle(subject)}
                                        className="text-xs italic"
                                    />
                                )}
                                {renderIf(
                                    notes,
                                    <div className="flex items-start gap-1 text-xs italic">
                                        <span className="flex items-center gap-1 text-color-500 whitespace-no-wrap">
                                            <GoDotFill size={10} className="text-color-500"/>
                                            {`${t('note')} : `}
                                        </span>
                                        <span className="font-semibold text-color-100 whitespace-pre-line">
                                            {notes}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="absolute top-5 right-5 contextIcon">
                        {renderContextMenu(item)}
                    </div>
                </div>
            )}
        </Translate>
    )
}
